/**
 * Recovery-time quality tier selection.
 *
 * The initial tier (see `selectInitialQualityTier`) answers "what should this guest start
 * on?" from the connection hint it reported. This module answers a different question:
 * "this guest just lost its connection and is coming back — what should it come back on?"
 *
 * The answer is derived from the camera that is actually in use rather than from a fresh
 * connection hint, because the guest's page-load timings are frozen at load and cannot
 * describe the post-failover link (see connectionQualityProbe). The camera in use is
 * observable, so stepping down from it is both authoritative and independent of whether
 * the guest's hint survived the signaling server.
 */

import {
  QUALITY_TIER_RESOLUTION,
  QUALITY_TIER_SHADOWCAM,
  TARGET_FRAMERATE,
  type InitialQualitySelection,
  type InitialTierInput,
  type QualityTier,
  type StreamResolution,
} from './webrtcLatencyContracts';
import { MobileTierPolicy } from './mobileTier/MobileTierPolicy';

/**
 * PRODUCT DECISION — currently ON.
 *
 * When true, a guest that re-establishes its connection comes back one tier lower than the
 * tier it dropped from: a link that could not hold its resolution through a network change
 * has not earned it back. Turning this off makes a recovery keep whatever tier was already
 * in use, which is the pre-recovery-demotion behaviour.
 *
 * This applies to DESKTOP as well as mobile. Desktop used to be pinned to 720p on every path,
 * which left it with a single bitrate number as its entire response to a bad link — and a
 * bitrate cut at 720p60 runs out of picture long before it runs out of link. A desktop guest
 * whose connection fails is in the same position as a mobile one: the ladder is about what the
 * LINK can carry, not about what kind of device is on the other end.
 */
export const DEMOTE_ONE_TIER_ON_RECOVERY = true;

/** @deprecated Retained for existing importers; recovery demotion is no longer mobile-only. */
export const DEMOTE_ONE_TIER_ON_MOBILE_RECOVERY = DEMOTE_ONE_TIER_ON_RECOVERY;

/** Highest to lowest. A demotion moves exactly one place down this ladder. */
export const QUALITY_TIER_LADDER: readonly QualityTier[] = ['top', 'middle', 'bottom'];

/** The ShadowCam a tier captures from. */
export type TierShadowcamName = (typeof QUALITY_TIER_SHADOWCAM)[QualityTier];

/** Tier that owns this ShadowCam, or null for a camera outside the tier ladder. */
export function tierForShadowcam(shadowcamName: string | null | undefined): QualityTier | null {
  if (!shadowcamName) return null;
  for (const tier of QUALITY_TIER_LADDER) {
    if (QUALITY_TIER_SHADOWCAM[tier] === shadowcamName) return tier;
  }
  return null;
}

/** One step down the ladder. The bottom tier is the floor, never below it. */
export function demoteOneTier(tier: QualityTier): QualityTier {
  const index = QUALITY_TIER_LADDER.indexOf(tier);
  if (index < 0) return tier;
  return QUALITY_TIER_LADDER[Math.min(index + 1, QUALITY_TIER_LADDER.length - 1)] ?? tier;
}

/**
 * A peer's contribution to the shared capture decision: either what its metadata implies, or
 * the tier a recovery demotion actually put it on.
 */
export type SharedTierInput = InitialTierInput & { demotedTier?: QualityTier };

/**
 * Lowest-common-denominator capture tier for a shared ShadowCam.
 * desktop → top; mobile good → middle; mobile questionable → bottom; and a recovery-demoted
 * peer contributes the tier it was actually demoted to, whatever device it is.
 * Empty set → null (keep the camera already in use).
 */
export function selectSharedCaptureTier(
  peers: ReadonlyArray<SharedTierInput>,
): InitialQualitySelection | null {
  if (peers.length === 0) return null;
  let worst: QualityTier = 'top';
  const policy = new MobileTierPolicy();
  for (const peer of peers) {
    // An explicit demoted tier wins over anything derivable from the metadata. Demotion used
    // to be expressed by pretending the peer was a questionable mobile one, which always means
    // the floor — that could not describe a desktop guest stepped down to 480p, and would have
    // dragged the whole shared capture to 360p on its behalf.
    const tier = policy.select({
      device: peer.isMobile === true ? 'mobile' : peer.isMobile === false ? 'desktop' : 'unknown',
      connectionQuestionable: peer.connectionQuestionable,
      retainedTier: peer.demotedTier,
    }).tier;
    if (QUALITY_TIER_LADDER.indexOf(tier) > QUALITY_TIER_LADDER.indexOf(worst)) {
      worst = tier;
    }
  }
  return {
    tier: worst,
    shadowcam: QUALITY_TIER_SHADOWCAM[worst],
    resolution: QUALITY_TIER_RESOLUTION[worst],
    targetFramerate: TARGET_FRAMERATE,
  };
}

/** True when `needed` is a higher-quality ladder step than `current` (promotion only). */
export function isSharedTierPromotion(
  currentShadowcam: string | null | undefined,
  neededShadowcam: string | null | undefined,
): boolean {
  if (!currentShadowcam || !neededShadowcam || currentShadowcam === neededShadowcam) {
    return false;
  }
  const currentTier = tierForShadowcam(currentShadowcam);
  const neededTier = tierForShadowcam(neededShadowcam);
  if (currentTier === null || neededTier === null) return false;
  return QUALITY_TIER_LADDER.indexOf(neededTier) < QUALITY_TIER_LADDER.indexOf(currentTier);
}

export type RecoveryTierReason =
  | 'demoted-one-step'
  | 'already-lowest-tier'
  /** Current camera is outside the tier ladder, so there is no step to take from it. */
  | 'unknown-current-shadowcam'
  | 'retained-floor'
  | 'demotion-disabled';

export type RecoveryTierSelection = {
  /** Tier to stream on, or null when the current camera is outside the ladder. */
  tier: QualityTier | null;
  /**
   * Camera to stream from, or null when it cannot be derived. A non-null value equal to
   * `currentShadowcam` means "no swap"; callers must treat null as "decide some other way".
   */
  shadowcam: string | null;
  fromTier: QualityTier | null;
  demoted: boolean;
  reason: RecoveryTierReason;
};

export type RecoveryTierInput = {
  isMobile: boolean;
  /** Camera currently captured by the host, from MediaCoordinator. */
  currentShadowcam: string | null;
  /** Peer-specific recovery ceiling retained even if a shared capture changed meanwhile. */
  retainedTier?: QualityTier;
};

/**
 * Tier for a guest that is re-establishing its connection.
 *
 * Steps down exactly one tier from the camera in use — never straight to the floor — and stops
 * once it is on the floor. Device type does not enter into it: a desktop guest that dropped
 * from 720p comes back on 480p, the same way a mobile guest that dropped from 480p comes back
 * on 360p. The ladder describes what the link proved it could carry.
 */
export function selectRecoveryQualityTier(input: RecoveryTierInput): RecoveryTierSelection {
  const fromTier = tierForShadowcam(input.currentShadowcam);

  if (!DEMOTE_ONE_TIER_ON_RECOVERY) {
    return {
      tier: fromTier,
      shadowcam: input.currentShadowcam,
      fromTier,
      demoted: false,
      reason: 'demotion-disabled',
    };
  }

  if (fromTier === null) {
    if (input.retainedTier) {
      return {
        tier: input.retainedTier,
        shadowcam: QUALITY_TIER_SHADOWCAM[input.retainedTier],
        fromTier: null,
        demoted: false,
        reason: 'retained-floor',
      };
    }
    return {
      tier: null,
      shadowcam: null,
      fromTier: null,
      demoted: false,
      reason: 'unknown-current-shadowcam',
    };
  }

  const demotedTier = demoteOneTier(fromTier);
  const retained = input.retainedTier;
  const retainsFloor = retained !== undefined
    && QUALITY_TIER_LADDER.indexOf(retained) > QUALITY_TIER_LADDER.indexOf(demotedTier);
  const target = retainsFloor ? retained : demotedTier;
  return {
    tier: target,
    shadowcam: QUALITY_TIER_SHADOWCAM[target],
    fromTier,
    demoted: target !== fromTier,
    reason: retainsFloor ? 'retained-floor' : target === fromTier ? 'already-lowest-tier' : 'demoted-one-step',
  };
}

/** Capture resolution of a tier, for logging the demotion in human terms. */
export function resolutionForTier(tier: QualityTier): StreamResolution {
  return QUALITY_TIER_RESOLUTION[tier];
}
